/**
 * Set / change password dialog for the profile Security card.
 *
 * Talks to `POST /api/me/password` (session-authenticated — no email
 * round-trip). When the user already has a password, multipass demands
 * proof of knowledge, so the dialog adds a "Current password" field.
 * Server-side errors (weak password, wrong current password) surface
 * via the thrown ApiError message, which FormDialog renders inline.
 */

import { FormDialog } from '@ordo/ui/form-dialog'
import { FormField } from '@ordo/ui/form-field'
import { PasswordInput } from '@ordo/ui/password-input'
import { toast } from '@ordo/ui/sonner'
import { useState } from 'react'

import { useSetPasswordMutation } from '@/api/me'

const MIN_PASSWORD_LENGTH = 8

export interface SetPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Whether a password already exists — switches copy + requires the current password. */
  hasPassword: boolean
}

export function SetPasswordDialog({ open, onOpenChange, hasPassword }: SetPasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const mutation = useSetPasswordMutation()

  const mismatch = confirmPassword !== '' && confirmPassword !== newPassword
  const submitDisabled =
    newPassword.length < MIN_PASSWORD_LENGTH ||
    confirmPassword !== newPassword ||
    (hasPassword && currentPassword === '')

  const reset = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const onSubmit = async () => {
    await mutation.mutateAsync({
      newPassword,
      currentPassword: hasPassword ? currentPassword : undefined,
    })
    toast.success(hasPassword ? 'Password updated' : 'Password set')
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={hasPassword ? 'Change password' : 'Set password'}
      description={
        hasPassword
          ? 'Choose a new password for signing in.'
          : 'Set a password so you can sign in without waiting for an email link.'
      }
      submitLabel={hasPassword ? 'Update password' : 'Set password'}
      submitDisabled={submitDisabled}
      onSubmit={onSubmit}
      onReset={reset}>
      {hasPassword && (
        <FormField label="Current password" htmlFor="set-password-current">
          <PasswordInput
            id="set-password-current"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
        </FormField>
      )}
      <FormField
        label="New password"
        htmlFor="set-password-new"
        helperText={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
        <PasswordInput
          id="set-password-new"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
        />
      </FormField>
      <FormField
        label="Confirm new password"
        htmlFor="set-password-confirm"
        helperText={mismatch ? 'Passwords do not match.' : undefined}>
        <PasswordInput
          id="set-password-confirm"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          aria-invalid={mismatch || undefined}
        />
      </FormField>
    </FormDialog>
  )
}
